import { Document, Page, Text, View, StyleSheet, Image, Svg, Path, Font, Circle } from '@react-pdf/renderer';
import { IInvoice, IInvoiceDetail } from '../../redux/invoice/invoiceSlice';
import { TableRowProps } from './Payments';
import { theme } from '../../theme/theme';
import FrameImage from '../../assets/img/V1/Frame.png';
import { InvoiceStatus } from '../../utils/enums/invoice-status.enum';

Font?.register({
  family: 'inter',
  fonts: [
    // eslint-disable-next-line global-require,import/extensions
    { src: require('../../assets/fonts/Inter_Regular.ttf'), fontWeight: 400 },
    // eslint-disable-next-line global-require,import/extensions
    { src: require('../../assets/fonts/Inter_Medium.ttf'), fontWeight: 800 },
  ],
});
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    padding: 30,
    fontFamily: 'inter',
    color: theme.palette.common.black,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  twoColumn: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  companyColumn: {
    width: '45%',
    textAlign: 'right',
  },
  bold: {
    fontWeight: 'bold',
  },
  table: {
    fontSize: 12,
    width: '100%',
    marginVertical: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    marginBottom: 8,
    backgroundColor: theme.palette.shades.tableHeadColor,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  colDescription: {
    width: '55%',
    paddingRight: 10,
  },
  colQty: {
    width: '15%',
    textAlign: 'right',
  },
  colUnit: {
    width: '15%',
    textAlign: 'right',
  },
  colAmount: {
    width: '15%',
    textAlign: 'right',
  },
  flexRow: {
    flexDirection: 'row',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  column55: {
    width: '55%',
    padding: 10,
  },
  column45: {
    width: '45%',
    padding: 10,
  },
  grayBg: {
    backgroundColor: '#3348FF0A',
  },
  divider: {
    height: 1,
    marginVertical: 8,
  },
});

const SuccessIcon = () => (
  <View>
    <Svg width="10" height="10" viewBox="0 0 8 8" fill="#BEFEC7">
      <Path d="M0.5 5L2.25 6.75L7.5 1.25" stroke="#00A81C" stroke-linecap="round" stroke-linejoin="round" />
    </Svg>
  </View>
);
const ErrorImportantIcon = () => (
  <View>
    <Svg width="16" height="16" viewBox="0 0 16 16">
      <Circle cx="8.00001" cy="7.9987" r="6.66667" stroke="#DF0C3D" strokeWidth="1.5" />
      <Path d="M7.99465 10H8.00064" stroke="#DF0C3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M8 8L8 5.33333" stroke="#DF0C3D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  </View>
);
const WarningImportantIcon = () => (
  <View>
    <Svg width="10" height="10" viewBox="0 0 17 16" fill={theme.palette.shades.warningBrightLight}>
      <Path
        d={
          'M8.66667 14.6673C12.3486 14.6673 15.3333 11.6825 15.3333 8.00065C15.3333 4.31875 12.3486 1.33398 8.66667 ' +
          '1.33398C4.98477 1.33398 2 4.31875 2 8.00065C2 11.6825 4.98477 14.6673 8.66667 14.6673Z'
        }
        stroke="#DB6E00"
        stroke-width="1.5"
      />
      <Path
        d={
          'M8.67212 7.00545C8.11983 7.00545 7.67212 7.45317 7.67212 8.00545C7.67212 8.55774 8.11983 9.00545 8.67212 ' +
          '9.00545C9.2244 9.00545 9.67212 8.55774 9.67212 8.00545C9.67212 7.45317 9.2244 7.00545 8.67212 ' +
          '7.00545ZM8.67212 7.00545V4.66602M10.6767 10.0132L9.37764 8.71414'
        }
        stroke="#DB6E00"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </Svg>
  </View>
);

const PaymentIndicator = ({ paymentStatus }: { paymentStatus: string }) => {
  let colorSet = {
    backgroundColor: theme.palette.shades.successBrightLight,
    icon: <SuccessIcon />,
    textColor: theme.palette.shades.successBrightMain,
    text: 'Paid',
  };

  if (paymentStatus === InvoiceStatus.OVERDUE) {
    colorSet = {
      backgroundColor: theme.palette.shades.errorBrightLight,
      icon: <ErrorImportantIcon />,
      textColor: theme.palette.shades.errorBrightMain ?? '#DF0C3D',
      text: 'Overdue',
    };
  } else if (paymentStatus === InvoiceStatus.PAID) {
    colorSet = {
      backgroundColor: theme.palette.shades.successBrightLight,
      icon: <SuccessIcon />,
      textColor: theme.palette.shades.successBrightMain,
      text: 'Paid',
    };
  } else if (paymentStatus === InvoiceStatus.TRIAL) {
    colorSet = {
      backgroundColor: theme.palette.shades.warningBrightLight,
      icon: <WarningImportantIcon />,
      textColor: theme.palette.shades.warningBrightMain,
      text: 'Trial',
    };
  } else if (paymentStatus === InvoiceStatus.TRIAL_OVERDUE) {
    colorSet = {
      backgroundColor: theme.palette.shades.errorBrightLight,
      icon: <ErrorImportantIcon />,
      textColor: theme.palette.shades.errorBrightMain ?? '#DF0C3D',
      text: 'Trial-Overdue',
    };
  } else if (paymentStatus === InvoiceStatus.TRIAL_PAID) {
    colorSet = {
      backgroundColor: theme.palette.shades.warningBrightLight,
      icon: <WarningImportantIcon />,
      textColor: theme.palette.shades.warningBrightMain,
      text: 'Trial-Paid',
    };
  }

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: colorSet.backgroundColor,
        paddingRight: 4,
        paddingLeft: 2,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 8,
        alignContent: 'center',
        alignItems: 'center',
        height: 17,
        fontSize: 8,
        fontWeight: 400,
      }}
    >
      <View style={{ marginRight: 4 }}>{colorSet.icon}</View>
      <Text style={{ color: colorSet.textColor, fontSize: 10 }}>{colorSet.text}</Text>
    </View>
  );
};

const FieldRow = ({
  label,
  value,
  component,
}: {
  label: string;
  value?: string | number;
  component?: React.ReactNode;
}) => (
  <View style={[styles.flexRow, styles.spaceBetween, { height: 18 }]}>
    <Text>{label}</Text>
    {component || <Text style={styles.bold}>{value}</Text>}
  </View>
);

const Invoice = ({
  invoiceDetails,
  invoice,
  rows,
}: {
  invoiceDetails: IInvoiceDetail;
  invoice: IInvoice;
  rows: TableRowProps[];
}) => {
  const [day, month, year] = (invoice?.invoiceDate?.split('/') ?? []).map(Number);
  const invoiceDate = new Date(year, month - 1, day);
  const fromDate = new Date(invoiceDate);
  fromDate.setMonth(invoiceDate.getMonth() - 1);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            <Text style={{ fontSize: 24, fontWeight: 'bold' }}>Invoice</Text>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={styles.companyColumn}>
              <Text style={{ fontWeight: 'bold', fontSize: 12 }}>Porolis Technologies Pte Ltd</Text>
              <Text style={{ fontWeight: 'normal', fontSize: 8 }}>14 Kung Chong Rd, #05-01 Lum Chang Building,</Text>
              <Text style={{ fontWeight: 'normal', fontSize: 8 }}>Singapore 159150 Singapore</Text>
            </View>
          </View>
        </View>

        <View style={styles.twoColumn}>
          <View style={styles.column55}>
            <FieldRow label="Invoice Number" value={invoice?.systemId} />
            <FieldRow label="Date of issue" value={invoice?.invoiceDate} />
            <FieldRow label="" value="" />
            <View style={[styles.divider, { backgroundColor: '#FFF' }]} />
            <FieldRow label="Bill To" value={invoiceDetails.companyName} />
            <FieldRow label="Account Number" value={invoiceDetails.accountId} />
          </View>

          <View style={[styles.column45, styles.grayBg]}>
            <FieldRow
              label="Payment Status"
              component={<PaymentIndicator paymentStatus={invoiceDetails?.status ?? ''} />}
            />
            <FieldRow
              label="Total Payable Amount"
              value={`$${
                Number.isNaN(parseFloat(invoiceDetails?.total?.toString() || ''))
                  ? '0.00'
                  : invoiceDetails?.total?.toFixed(2)
              }`}
            />
            <FieldRow label="Payment Due Date" value={invoiceDate.toLocaleDateString('en-GB')} />
            <View style={[styles.divider, { backgroundColor: theme.palette.common.black }]} />
            <View style={{ marginTop: 8 }}>
              <Text>Transaction Date</Text>
              <Text style={{ fontWeight: 'bold' }}>{invoiceDetails.paidDateAndTime ?? '-'}</Text>
            </View>
            <View style={{ marginTop: 8 }}>
              <Text>Transaction Number</Text>
              <Text style={{ fontWeight: 'bold' }}>{invoiceDetails.transactionId ?? '-'}</Text>
            </View>
          </View>
        </View>

        <View style={{ height: 24 }} />
        <View style={{ display: 'flex', flexDirection: 'row' }}>
          <View style={{ width: '50%', paddingRight: 20 }}>
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text>Billing Period </Text>
              <Text>{`${fromDate.toLocaleDateString('en-GB')} to ${invoiceDate.toLocaleDateString('en-GB')}`}</Text>
            </View>
          </View>
          <View style={{ width: '50%', textAlign: 'right', paddingLeft: 20 }} />
        </View>
        <View style={{ height: 16 }} />
        <View style={styles.tableHeader}>
          <Text style={styles.colDescription}>Description</Text>
          <Text style={styles.colQty}>Qty</Text>
          <Text style={styles.colUnit}>Unit Cost</Text>
          <Text style={styles.colAmount}>Price</Text>
        </View>

        {rows.map(({ rowKey, value, finalrow }) => (
          <View
            style={{
              ...styles.tableRow,
              borderBottomWidth: finalrow ? 0 : 1,
              borderTopWidth: finalrow ? 1 : 0,
              borderTopColor: theme.palette.common.black,
              borderBottomColor: theme.palette.shades.grayBorder,
            }}
            key={rowKey}
          >
            {finalrow ? (
              <>
                <Text style={styles.colDescription} />
                <Text style={styles.colDescription} />
                <Text style={{ width: '20%', textAlign: 'center' }}>Total</Text>
                <Text style={{ width: '20%', textAlign: 'right' }}>{value[1].name}</Text>
              </>
            ) : (
              value.map(({ key, name, align, bold, padding }) =>
                key === 'gem_passport' ? (
                  <View
                    style={[
                      align === 'right' ? styles.colAmount : styles.colDescription,
                      { display: 'flex', flexDirection: 'row', alignItems: 'center', padding },
                    ]}
                  >
                    <Text>{name}</Text>
                    <Image src={FrameImage} style={{ width: 12, height: 12, marginLeft: 6 }} />
                  </View>
                ) : (
                  <Text
                    key={`${rowKey}-${key}`}
                    style={[
                      align === 'right' ? styles.colAmount : styles.colDescription,
                      {
                        flexDirection: 'row',
                        alignItems: 'center',
                        fontWeight: bold ? 'bold' : '400',
                        padding,
                      },
                    ]}
                  >
                    {name}
                  </Text>
                ),
              )
            )}
          </View>
        ))}
      </Page>
    </Document>
  );
};
export default Invoice;
